import { Collapse as BlueprintCollapse } from '@blueprintjs/core';
import {
  ChevronDown,
  ChevronUp,
  Close,
  Edit,
  Renew,
  TrashCan,
  WarningFilled,
} from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  ConfidenceLevel,
  Icon,
  LoadingIcon,
  MenuItem,
  MenuItems,
  OutlinedIconButton,
  Textarea,
  VoiceboxLogo,
} from 'vet-bones/design-system/components';
import { isNotEqual } from 'vet-bones/utils';

import { projectsActionCreators } from 'src/actions/projects/actionCreators';
import { SidebarCard } from 'src/components/sidebarCard/SidebarCard';
import { TEST_IDS } from 'src/constants/testIds';
import { CompetencyQuestion } from 'src/constants/voicebox';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { commitDiagramModel } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { setModelCompetencyQuestions } from 'src/containers/ProjectDiagram/utils/setModelCompetencyQuestions';
import { projectSelector } from 'src/selectors/project';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';
import { getConfidenceLevel } from 'src/utils/voicebox/getConfidenceLevel';
import { useEvaluateQuestions } from 'src/utils/voicebox/hooks/useEvaluateQuestions';

import cardStyles from 'src/components/sidebarCard/sidebarCard.module.scss';
import sidebarStyles from 'src/containers/ProjectSidebar/projectSidebar.module.scss';

type ProjectSidebarQuestionCardProps = {
  editValue: string;
  idx: number;
  isEditing: boolean;
  isOpen: boolean;
  onEditQuestion: (idx: number | null, value?: string) => void;
  question: CompetencyQuestion;
  questions: CompetencyQuestion[];
  setIsOpen: (isOpen: boolean) => void;
};

export const ProjectSidebarQuestionCard: React.VFC<
  ProjectSidebarQuestionCardProps
> = ({
  editValue,
  idx,
  isEditing,
  isOpen,
  onEditQuestion,
  question,
  questions,
  setIsOpen,
}) => {
  const diagram = useDiagram();
  const dispatch = useReduxDispatch();

  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));

  const {
    evaluateQuestions,
    isLoadingEvaluations,
    loadingEvaluationIds,
    isLoadingQueries,
    loadingQueryIds,
  } = useEvaluateQuestions();

  const didEvaluateQuestion =
    question.evaluation &&
    project?.voiceboxEvaluatedCompetencyQuestions?.includes(question.id);

  const onCancelEdit = React.useCallback(() => {
    onEditQuestion(null);
  }, [onEditQuestion]);

  const onConfirmEdit = React.useCallback(() => {
    if (!diagram) {
      return;
    }

    const trimmed = editValue.trim();
    if (question.question === trimmed) {
      onEditQuestion(null);
      return;
    }

    const nextQuestions = [...questions];
    const nextQuestion = { id: question.id, question: trimmed };
    if (trimmed) {
      nextQuestions.splice(idx, 1, nextQuestion);
    } else {
      nextQuestions.splice(idx, 1);
    }

    onEditQuestion(null);

    if (isNotEqual(questions, nextQuestions)) {
      commitDiagramModel(diagram, setModelCompetencyQuestions(nextQuestions));
    }

    if (trimmed) {
      evaluateQuestions([nextQuestion]);
    }
  }, [
    diagram,
    editValue,
    evaluateQuestions,
    idx,
    onEditQuestion,
    question,
    questions,
  ]);

  const onDelete = React.useCallback(() => {
    if (!diagram) {
      return;
    }

    const nextQuestions = [...questions];
    nextQuestions.splice(idx, 1);
    commitDiagramModel(diagram, setModelCompetencyQuestions(nextQuestions));
  }, [diagram, idx, questions]);

  const wasEscKeyBlur = React.useRef(false);

  return isEditing ? (
    <SidebarCard
      actions={
        <OutlinedIconButton
          id="edit-card-cancel"
          leftIcon={Close}
          onClick={() => {
            onCancelEdit();
          }}
          testId={TEST_IDS.projectSidebar.questionCard.cancelButton}
        />
      }
      active
      className={sidebarStyles.projectSidebarQuestionCard}
      divId="edit-card"
    >
      <Textarea
        autoFocus
        growVertically
        id="competency-question"
        label=""
        minimal
        onBlur={(evt) => {
          const targetId = evt.relatedTarget?.id;
          const wasCardClick =
            targetId === 'edit-card' || targetId === 'edit-card-cancel';

          if (wasCardClick) {
            evt.currentTarget.focus();
          }

          if (wasCardClick || wasEscKeyBlur.current) {
            wasEscKeyBlur.current = false;
            return;
          }

          onConfirmEdit();
        }}
        onChange={(evt) => {
          onEditQuestion(idx, evt.target.value);
        }}
        onFocus={(evt) => {
          evt.currentTarget.select();
        }}
        onKeyDown={(evt) => {
          if (evt.key === 'Enter') {
            evt.currentTarget.blur();
          } else if (evt.key === 'Escape') {
            onCancelEdit();
            wasEscKeyBlur.current = true;
            evt.currentTarget.blur();
          }
        }}
        placeholder={copy.components.project.competencyQuestions.placeholder}
        rows={1}
        testId={TEST_IDS.projectSidebar.questionCard.textarea}
        value={editValue}
      />
    </SidebarCard>
  ) : (
    <SidebarCard
      // eslint-disable-next-line react/no-array-index-key
      key={idx}
      actions={
        <div className={cardStyles.sidebarCardMenuAlt}>
          {loadingEvaluationIds.has(question.id) ? (
            <Icon IconComponent={LoadingIcon} size="sm" />
          ) : (
            <ConfidenceLevel
              level={getConfidenceLevel(question.evaluation?.score)}
            />
          )}
        </div>
      }
      active={isOpen}
      className={sidebarStyles.projectSidebarQuestionCard}
      menu={
        <MenuItems className={sidebarStyles.projectSidebarMediumMenuItems}>
          <MenuItem
            disabled={isLoadingEvaluations || didEvaluateQuestion}
            leftIcon={Renew}
            onClick={() => {
              evaluateQuestions([question]);
            }}
            testId={TEST_IDS.projectSidebar.questionCard.evaluateButton}
            text={copy.components.project.competencyQuestions.evaluate}
          />
          <MenuItem
            disabled={
              loadingEvaluationIds.has(question.id) ||
              loadingQueryIds.has(question.id)
            }
            leftIcon={Edit}
            onClick={() => {
              onEditQuestion(idx, question.question);
            }}
            testId={TEST_IDS.projectSidebar.questionCard.editButton}
            text={copy.components.project.competencyQuestions.edit}
          />
          <MenuItem
            leftIcon={TrashCan}
            onClick={onDelete}
            testId={TEST_IDS.projectSidebar.questionCard.deleteButton}
            text={copy.components.project.competencyQuestions.delete}
          />
        </MenuItems>
      }
      menuTestId={TEST_IDS.projectSidebar.questionCard.menuButton}
      onClick={
        !isOpen
          ? () => {
              if (!window.getSelection()?.toString()) {
                setIsOpen(true);
              }
            }
          : undefined
      }
      title={
        <OutlinedIconButton
          leftIcon={isOpen ? ChevronUp : ChevronDown}
          onClick={() => setIsOpen(!isOpen)}
          testId={TEST_IDS.projectSidebar.questionCard.toggleButton}
        />
      }
    >
      <Body
        className="sd-selectable"
        size="md"
        testId={TEST_IDS.projectSidebar.questionCard.title}
      >
        {question.question}
      </Body>

      <BlueprintCollapse isOpen={isOpen}>
        <div className={sidebarStyles.projectSidebarQuestionCardDivider} />
        {question.evaluation?.tags.map((tag) => (
          <div
            key={tag}
            className={sidebarStyles.projectSidebarQuestionCardTag}
          >
            <Icon IconComponent={WarningFilled} size="sm" />
            <Body className="sd-selectable" size="sm">
              {copy.components.project.competencyQuestions.tags[tag] || tag}
            </Body>
          </div>
        ))}
        <Button
          disabled={isLoadingEvaluations || isLoadingQueries}
          leftIcon={
            loadingQueryIds.has(question.id) ? LoadingIcon : VoiceboxLogo
          }
          leftIconProps={{ theme: 'light' }}
          onClick={async () => {
            let updatedQuestion: CompetencyQuestion = question;
            if (!question.evaluation?.query) {
              const updatedQuestions = await evaluateQuestions([question], {
                waitForQueries: true,
              });
              updatedQuestion =
                updatedQuestions?.find((q) => q.id === question.id) || question;
            }

            if (updatedQuestion.evaluation) {
              dispatch(
                uiSlice.actions.toggledVoiceboxDrawerSummary({ isOpen: false })
              );
              dispatch(
                projectsActionCreators.openedProjectCompetencyQuestion({
                  id: projectId,
                  question: updatedQuestion,
                })
              );
              dispatch(uiSlice.actions.toggledVoiceboxDrawer({ isOpen: true }));
            }
          }}
          size="xs"
          testId={TEST_IDS.projectSidebar.questionCard.moreDetailsButton}
          variant="tertiary"
        >
          {copy.components.project.competencyQuestions.moreDetails}
        </Button>
      </BlueprintCollapse>
    </SidebarCard>
  );
};
